import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Download, PlayCircle, RotateCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PayNoticeDialog from "@/components/PayNoticeDialog";
import { describeNotice } from "@/lib/approvals";

function fmtINR(n) { return `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`; }
function monthLabel(m) { return dayjs(`${m}-01`).format("MMMM YYYY"); }

// Work out whether someone was on the books for the whole month. A joiner or
// leaver mid-month must never be paid a full month by accident, so these are
// the rows we insist on a figure for.
export function partMonth(e, month) {
  const start = dayjs(`${month}-01`);
  const end = start.endOf("month");
  const joined = e.join_date ? dayjs(e.join_date) : null;
  const leftRaw = e.last_working_day || e.exit_date || null;
  const left = leftRaw ? dayjs(leftRaw) : null;
  if (joined && joined.isValid() && joined.isAfter(end)) return { skip: true, reason: "joins later" };
  if (left && left.isValid() && left.isBefore(start)) return { skip: true, reason: "already left" };
  const from = joined && joined.isValid() && joined.isAfter(start) ? joined : start;
  const to = left && left.isValid() && left.isBefore(end) ? left : end;
  const full = end.date();
  const days = Math.max(0, to.diff(from, "day") + 1);
  let reason = "";
  if (from.isAfter(start)) reason = `joined ${from.format("D MMM")}`;
  else if (to.isBefore(end)) reason = `last day ${to.format("D MMM")}`;
  return { skip: false, days, full, part: days < full, reason };
}

export default function EmployeePayroll() {
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [employees, setEmployees] = useState([]);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [rows, setRows] = useState({});
  const [regen, setRegen] = useState(null);
  const [regenDays, setRegenDays] = useState("");
  const [regenAmount, setRegenAmount] = useState("");
  const [notice, setNotice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [emp, ps] = await Promise.all([
        api.get("/employees", { params: { status: "active" } }),
        api.get("/payslips", { params: { month } }),
      ]);
      setEmployees(emp.data || []);
      setSlips(ps.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]);

  const payable = useMemo(
    () => employees.map((e) => ({ e, pm: partMonth(e, month) })).filter((r) => !r.pm.skip),
    [employees, month]
  );
  const done = useMemo(() => new Set(slips.map((p) => p.employee_id)), [slips]);
  const monthTotal = useMemo(
    () => slips.reduce((s, p) => s + Number(p.total_payment ?? p.net_pay ?? 0), 0),
    [slips]
  );

  const openRun = () => {
    const next = {};
    // Pre-fill the days box for anyone who was not here the whole month, so
    // the common case is a glance rather than arithmetic.
    for (const { e, pm } of payable) next[e.id] = { days: pm.part ? String(pm.days) : "", amount: "" };
    setRows(next);
    setRunOpen(true);
  };

  const run = async () => {
    const missing = payable
      .filter(({ e, pm }) => pm.part && !(rows[e.id]?.days || rows[e.id]?.amount))
      .map(({ e }) => e.name);
    if (missing.length) { toast.error("Days or amount needed for: " + missing.join(", ")); return; }
    const days = {}; const amounts = {};
    for (const { e } of payable) {
      const r = rows[e.id] || {};
      if (String(r.amount ?? "").trim() !== "") amounts[e.id] = Number(r.amount);
      else if (String(r.days ?? "").trim() !== "") days[e.id] = Number(r.days);
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/payslips/generate?month=${encodeURIComponent(month)}`, { days, amounts });
      toast.success(`${monthLabel(month)}: ${data.created ?? 0} created, ${data.skipped ?? 0} left alone`);
      setRunOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doRegen = async () => {
    if (!regen) return;
    setBusy(true);
    try {
      await api.post("/payslips/generate-one", {
        employee_id: regen.employee_id,
        month: regen.month,
        days: String(regenDays).trim() === "" ? null : Number(regenDays),
        amount: String(regenAmount).trim() === "" ? null : Number(regenAmount),
        replace: true,
      });
      toast.success(`Rebuilt ${monthLabel(regen.month)} for ${regen.employee_snapshot?.name || ""}`);
      setRegen(null); setRegenDays(""); setRegenAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setRow = (id, k, v) => setRows((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const download = (p) => window.open(`${API_BASE}/payslips/${p.id}/pdf`, "_blank", "noopener,noreferrer");

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Payroll</h2>
          <p className="text-sm text-zinc-500">
            Run the month, then adjust anyone who needs part-month days or a set amount.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Input type="month" value={month} onChange={(ev) => setMonth(ev.target.value)}
                 className="h-9 w-40 border-zinc-300" data-testid="emp-payroll-month" />
          <Button size="sm" variant="outline" className="h-8 border-zinc-300 mr-2"
                  disabled={busy || loading} onClick={() => setNotice("ready")}
                  data-testid="emp-notify-ready">Payslip ready</Button>
          <Button size="sm" variant="outline" className="h-8 border-zinc-300 mr-2"
                  disabled={busy || loading} onClick={() => setNotice("paid")}
                  data-testid="emp-notify-paid">Record payment</Button>
          <Button onClick={openRun} disabled={busy || loading}
                  className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="emp-payroll-run">
            <PlayCircle className="h-4 w-4 mr-1" /> Run payroll
          </Button>
        </div>
      </div>

      <Card className="border-zinc-200 shadow-none">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">{monthLabel(month)}</CardTitle>
          <div className="text-sm text-zinc-500">
            {slips.length} of {payable.length} paid
            <span className="ml-3 font-semibold text-emerald-700 tabular-nums">{fmtINR(monthTotal)}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead className="text-right">Payable</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slips.map((p) => (
                  <TableRow key={p.id} data-testid={`emp-payroll-row-${p.id}`}>
                    <TableCell className="text-sm font-medium">
                      {p.employee_snapshot?.name || "—"}
                      <div className="text-[11px] text-zinc-500">{p.employee_snapshot?.employee_code}</div>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      {p.amount_override
                        ? <Badge variant="outline" className="text-[11px] border-amber-200 text-amber-700 bg-amber-50">set amount</Badge>
                        : (p.days_worked ?? "full month")}
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums font-semibold text-emerald-700">
                      {fmtINR(p.total_payment ?? p.net_pay)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="h-8 border-zinc-300"
                              onClick={() => download(p)} data-testid={`emp-payroll-pdf-${p.id}`}>
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 border-zinc-300 ml-2"
                              onClick={() => { setRegen(p); setRegenDays(String(p.days_worked ?? "")); setRegenAmount(""); }}
                              data-testid={`emp-payroll-regen-${p.id}`}>
                        <RotateCw className="h-4 w-4 mr-1" /> Redo
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!slips.length && (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-zinc-500 py-10">
                    {loading ? "Loading…" : `No payslips for ${monthLabel(month)} yet. Run payroll to create them.`}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PayNoticeDialog
        open={!!notice} onOpenChange={(v) => setNotice(v ? notice : null)}
        mode={notice} busy={busy}
        rows={(slips || []).map((sl) => {
          const who = (employees || []).find((e) => e.id === sl.employee_id) || {};
          return {
            id: sl.id, name: who.name || sl.employee_id,
            amount: sl.total_payment ?? sl.net_pay,
            notified_ready_at: sl.notified_ready_at,
            notified_paid_at: sl.notified_paid_at, paid_on: sl.paid_on,
          };
        })}
        onConfirm={async (ids, when, alsoEmail) => {
          setBusy(true);
          try {
            const url = notice === "paid"
              ? "/payslips/mark-paid" : "/payslips/pay-notice";
            const body = notice === "paid"
              ? { payslip_ids: ids, payment_date: when, notify: alsoEmail }
              : { payslip_ids: ids, kind: "ready" };
            const { data } = await api.post(url, body);
            toast.success(describeNotice(data, notice));
            (data.skipped || []).forEach((x) =>
              toast.message(`${x.name} skipped`, { description: x.reason }));
            (data.failed || []).forEach((x) =>
              toast.error(`${x.name}: ${x.error}`));
            setNotice(null);
            load();
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setBusy(false); }
        }} />

      <Dialog open={runOpen} onOpenChange={(v) => !v && setRunOpen(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Run payroll — {monthLabel(month)}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 px-1">
            Leave both boxes empty to pay a whole month. Days pro-rates the salary on file;
            an amount overrides it outright. Anyone who joined or left mid-month is flagged.
          </p>
          <div className="max-h-[50vh] overflow-y-auto border border-zinc-200 rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="w-28">Days</TableHead>
                  <TableHead className="w-36">Amount</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payable.map(({ e, pm }) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">
                      <span className="font-medium">{e.name}</span>
                      {pm.part && (
                        <span className="ml-2 inline-flex items-center text-[11px] text-amber-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />{pm.reason} · {pm.days}/{pm.full} days
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input type="number" min="0" max={pm.full} className="h-8 border-zinc-300"
                             value={rows[e.id]?.days ?? ""} placeholder={String(pm.full)}
                             onChange={(ev) => setRow(e.id, "days", ev.target.value)}
                             data-testid={`emp-run-days-${e.id}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" min="0" step="0.01" className="h-8 border-zinc-300"
                             value={rows[e.id]?.amount ?? ""} placeholder="full pay"
                             onChange={(ev) => setRow(e.id, "amount", ev.target.value)}
                             data-testid={`emp-run-amount-${e.id}`} />
                    </TableCell>
                    <TableCell className="text-[11px] text-zinc-500">
                      {done.has(e.id) ? "already paid" : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-300" onClick={() => setRunOpen(false)}>Cancel</Button>
            <Button onClick={run} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="emp-run-confirm">
              {busy ? "Running…" : "Generate payslips"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!regen} onOpenChange={(v) => !v && setRegen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rebuild {regen ? monthLabel(regen.month) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-1">
            <p className="text-sm text-zinc-500">
              {regen?.employee_snapshot?.name}. An amount wins over days. A released payslip cannot be rebuilt.
            </p>
            <div>
              <label className="text-sm text-zinc-700">Days worked</label>
              <Input type="number" min="0" value={regenDays} placeholder="whole month"
                     onChange={(ev) => setRegenDays(ev.target.value)}
                     className="h-9 border-zinc-300" data-testid="emp-regen-days" />
            </div>
            <div>
              <label className="text-sm text-zinc-700">Or set the amount</label>
              <Input type="number" min="0" step="0.01" value={regenAmount} placeholder="from salary on file"
                     onChange={(ev) => setRegenAmount(ev.target.value)}
                     className="h-9 border-zinc-300" data-testid="emp-regen-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-300" onClick={() => setRegen(null)}>Cancel</Button>
            <Button onClick={doRegen} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="emp-regen-confirm">{busy ? "Working…" : "Rebuild"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
